import { Modal, DatePicker, Checkbox, Button } from "antd";
import { Copy, Truck, Package, Calendar } from "lucide-react";
import { useState } from "react";
import { EstimateService } from "~/types/estimate";
import dayjs, { Dayjs } from "dayjs";

interface DuplicateEstimateServiceModalProps {
  service: EstimateService | null;
  open: boolean;
  onClose: () => void;
  onDuplicate: (options: DuplicateEstimateServiceOptions) => void;
}

export interface DuplicateEstimateServiceOptions {
  includeVessel: boolean;
  includeInventoryTemplates: boolean;
  serviceDate: Dayjs | null;
}

export const DuplicateEstimateServiceModal: React.FC<DuplicateEstimateServiceModalProps> = ({
  service,
  open,
  onClose,
  onDuplicate,
}) => {
  const [includeVessel, setIncludeVessel] = useState(true);
  const [includeInventoryTemplates, setIncludeInventoryTemplates] = useState(true);
  const [serviceDate, setServiceDate] = useState<Dayjs | null>(dayjs());

  const handleDuplicate = () => {
    onDuplicate({
      includeVessel,
      includeInventoryTemplates,
      serviceDate,
    });
    resetState();
  };

  const resetState = () => {
    setIncludeVessel(true);
    setIncludeInventoryTemplates(true);
    setServiceDate(dayjs());
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!service) return null;

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <Copy className="text-blue-600" size={20} />
          <span>Duplicate Estimate Service</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      <div className="py-4">
        {/* Original Service Info */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg border">
          <div className="text-sm text-gray-500 mb-1">Duplicating from:</div>
          <div className="font-medium">
            {service.serviceType?.replace(/_/g, " ")}
          </div>
          {service.serviceDate && (
            <div className="text-sm text-gray-600">
              Original date: {dayjs(service.serviceDate).format("MMM D, YYYY")}
            </div>
          )}
        </div>

        {/* New Service Date */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-3">
            New Service Date
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              <Calendar size={12} className="inline mr-1" />
              Service Date
            </label>
            <DatePicker
              value={serviceDate}
              onChange={setServiceDate}
              className="w-full"
              format="MMM D, YYYY"
            />
          </div>
        </div>

        {/* Copy Options */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-3">
            Include in duplicate:
          </div>
          <div className="space-y-3">
            {/* Vessel Option */}
            <div
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                includeVessel ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
              onClick={() => setIncludeVessel(!includeVessel)}
            >
              <Checkbox checked={includeVessel} onChange={(e) => setIncludeVessel(e.target.checked)}>
                <div className="flex items-center space-x-2 ml-1">
                  <Truck size={16} className={includeVessel ? "text-blue-600" : "text-gray-400"} />
                  <div>
                    <div className="font-medium">Same Vessel</div>
                    <div className="text-xs text-gray-500">
                      {service.vessel?.name || "No vessel assigned"}
                    </div>
                  </div>
                </div>
              </Checkbox>
            </div>

            {/* Inventory Templates Option */}
            <div
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                includeInventoryTemplates ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
              onClick={() => setIncludeInventoryTemplates(!includeInventoryTemplates)}
            >
              <Checkbox checked={includeInventoryTemplates} onChange={(e) => setIncludeInventoryTemplates(e.target.checked)}>
                <div className="flex items-center space-x-2 ml-1">
                  <Package size={16} className={includeInventoryTemplates ? "text-blue-600" : "text-gray-400"} />
                  <div>
                    <div className="font-medium">Same Inventory Templates</div>
                    <div className="text-xs text-gray-500">
                      {service.inventoryTemplates && service.inventoryTemplates.length > 0
                        ? `${service.inventoryTemplates.length} template${service.inventoryTemplates.length > 1 ? "s" : ""}`
                        : "No templates assigned"}
                    </div>
                  </div>
                </div>
              </Checkbox>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-sm text-yellow-800">
            The duplicate will be created as a <strong>new estimate service</strong>.
            All other details (location, duration, staff count, notes) will be copied.
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleDuplicate}
            className="bg-blue-600 hover:bg-blue-700"
            icon={<Copy size={14} />}
          >
            Duplicate Service
          </Button>
        </div>
      </div>
    </Modal>
  );
};
